
// SearchPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Header from '../components/Header'; // Import Header component
import Footer from '../components/Footer'; // Import Footer component
import Banner from '../components/Banner'; // Import Banner component
import InfoBanner from '../components/InfoBanner';
import Tooltip from '../components/tooltip'; // Import Tooltip component


import { appUrl } from '../properties/properties';

const SearchPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const initialText = queryParams.get('text') || '';
    const initialVendor = queryParams.get('vendor') || '';
    const initialProduct = queryParams.get('product') || '';
    const initialAssigner = queryParams.get('assigner') || '';
    const initialAdvisory = queryParams.get('advisory') || '';
    const initialFromDate = queryParams.get('fromDate') || '';
    const initialToDate = queryParams.get('toDate') || '';
    const initialFromUpdatedDate = queryParams.get('fromUpdatedDate') || '';
    const initialToUpdatedDate = queryParams.get('toUpdatedDate') || '';
    const initialFromScore = queryParams.has('fromScore') ? +queryParams.get('fromScore') : 0.0;
    const initialToScore = queryParams.has('toScore') ? +queryParams.get('toScore') : 10.0;
    const initialFromEpss = queryParams.has('fromEpss') ? +queryParams.get('fromEpss') : 0.0;
    const initialToEpss = queryParams.has('toEpss') ? +queryParams.get('toEpss') : 100.0;
    const initialExploited = queryParams.get('exploited') === 'true';
    const initialPage = queryParams.has('page') ? +queryParams.get('page') : 0;

    // Determine if filter panel should auto-open based on URL params
    const hasFilterParams = initialVendor || initialProduct || initialAssigner || initialAdvisory
        || initialFromDate || initialToDate || initialFromUpdatedDate || initialToUpdatedDate
        || initialFromScore !== 0.0 || initialToScore !== 10.0
        || initialFromEpss !== 0.0 || initialToEpss !== 100.0
        || initialExploited;

    const updateUrlParams = (activeFilters, activePage) => {
        const params = new URLSearchParams();
        if (activeFilters.text) params.set('text', activeFilters.text);
        if (activeFilters.vendor) params.set('vendor', activeFilters.vendor);
        if (activeFilters.product) params.set('product', activeFilters.product);
        if (activeFilters.assigner) params.set('assigner', activeFilters.assigner);
        if (activeFilters.advisory) params.set('advisory', activeFilters.advisory);
        if (activeFilters.fromDate) params.set('fromDate', activeFilters.fromDate);
        if (activeFilters.toDate) params.set('toDate', activeFilters.toDate);
        if (activeFilters.fromUpdatedDate) params.set('fromUpdatedDate', activeFilters.fromUpdatedDate);
        if (activeFilters.toUpdatedDate) params.set('toUpdatedDate', activeFilters.toUpdatedDate);
        if (Number(activeFilters.fromScore) !== 0.0 || Number(activeFilters.toScore) !== 10.0) {
            params.set('fromScore', activeFilters.fromScore);
            params.set('toScore', activeFilters.toScore);
        }
        if (Number(activeFilters.fromEpss) !== 0.0 || Number(activeFilters.toEpss) !== 100.0) {
            params.set('fromEpss', activeFilters.fromEpss);
            params.set('toEpss', activeFilters.toEpss);
        }
        if (activeFilters.exploited) params.set('exploited', 'true');
        if (activePage > 0) params.set('page', activePage);
        const search = params.toString();
        navigate('/search' + (search ? '?' + search : ''), { replace: true });
    };

    const [filters, setFilters] = useState({
        assigner: initialAssigner,
        product: initialProduct,
        vendor: initialVendor,
        advisory: initialAdvisory,
        text: initialText,
        fromDate: initialFromDate,
        toDate: initialToDate,
        fromUpdatedDate: initialFromUpdatedDate,
        toUpdatedDate: initialToUpdatedDate,
        fromKEVDate: '',
        toKEVDate: '',
        fromScore: initialFromScore,
        toScore: initialToScore,
        fromEpss: initialFromEpss,
        toEpss: initialToEpss,
        exploited: initialExploited
    });
    console.log('filters ', filters);
    const [results, setResults] = useState([]);
    const [isFilterVisible, setIsFilterVisible] = useState(!!hasFilterParams);
    const [isLoading, setIsLoading] = useState(false); // New state variable to track loading status
    const [page, setPage] = useState(initialPage); // Pagination state
    const [size, setSize] = useState(10); // Items per page
    const [totalPages, setTotalPages] = useState(0); // Total pages state
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [isDateFilterVisible, setIsDateFilterVisible] = useState(!!(initialFromDate || initialToDate));
    const [fromUpdatedDate, setFromUpdatedDate] = useState(null);
    const [toUpdatedDate, setToUpdatedDate] = useState(null);
    const [isDateUpdatedFilterVisible, setIsDateUpdatedFilterVisible] = useState(!!(initialFromUpdatedDate || initialToUpdatedDate));
    const [fromKEVDate, setFromKEVDate] = useState(null);
    const [toKEVDate, setToKEVDate] = useState(null);
    const [isDateKEVFilterVisible, setIsDateKEVFilterVisible] = useState(false);
    const [fromScore, setFromScore] = useState(initialFromScore);
    const [toScore, setToScore] = useState(initialToScore);
    const [fromEpss, setFromEpss] = useState(initialFromEpss);
    const [toEpss, setToEpss] = useState(initialToEpss);
    const [isExploited, setIsExploited] = useState(false);
    const [kevMap, setKevMap] = useState({});
    const [honeypotMap, setHoneypotMap] = useState({});
    const [isScoreFilterVisible, setIsScoreFilterVisible] = useState(false);
    const [isEpssFilterVisible, setIsEpssFilterVisible] = useState(false);
    const [errorMessageDateRange, setErrorMessageDateRange] = useState('');
    const [errorMessageUpdatedDateRange, setErrorMessageUpdatedDateRange] = useState('');
    const [errorMessageKEVDateRange, setErrorMessageKEVDateRange] = useState('');
    const [errorMessageScoreRange, setErrorMessageScoreRange] = useState('');
    const [errorMessageEpssRange, setErrorMessageEpssRange] = useState('');
    const [searchError, setSearchError] = useState('');

    const abortControllerRef = useRef();

    const fetchVulnerabilities = async (customFilters = {}) => {
        if (abortControllerRef.current){
            abortControllerRef.current.abort();
            setIsLoading(false);
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const { signal } = controller;

        try {
            setIsLoading(true);
            setSearchError('');
            const params = new URLSearchParams({
                ...filters,
                ...customFilters, // Merge custom filters with existing filters
                page,
                size,
            });
            const queryParams = params.toString();
            console.log(`Fetching data with query: ${queryParams}`); // Debugging

            const fullUrl = `${appUrl}/search?${queryParams}`;

            const response = await fetch(fullUrl, { signal });

            // The status has to be read before the body is treated as results.
            // /search answers 400 for a parameter it will not build a query for, and
            // that body is {"error": "..."} with no items. Passing it straight to
            // setResults put undefined into `results`, which threw on the next render
            // and took the whole page to the ErrorBoundary -- that is the "Application
            // Unavailable" reported in ENISASUP-806, from a URL as ordinary as a
            // mistyped date.
            if (!response.ok) {
                setResults([]);
                setTotalPages(0);
                setSearchError(await readSearchError(response));
                setIsLoading(false);
                return;
            }

            const data = await response.json();

            // A body that parses but is not the shape we expect must not be able to
            // break the page either: everything below assumes an array.
            setResults(Array.isArray(data.items) ? data.items : []);
            setTotalPages(Math.ceil((Number(data.total) || 0) / size));
            setIsLoading(false);
        } catch (error) {
            if (error.name === 'AbortError') {
                // Superseded by a newer request, which owns the loading state now.
                return;
            }
            setIsLoading(false);
            setResults([]);
            setTotalPages(0);
            setSearchError('The search could not be completed. Please try again.');
            console.error('Error fetching vulnerabilities:', error);
        }
    };

    /**
     * The message to show for a rejected search. The API sends {"error": "..."} and
     * names the offending parameter, which is far more use than a generic line, so it
     * is preferred when present. Anything else falls back: an intermediary that never
     * reached the application answers with its own HTML, not our JSON.
     */
    const readSearchError = async (response) => {
        try {
            const body = await response.json();
            if (body && typeof body.error === 'string' && body.error.trim()) {
                return body.error;
            }
        } catch (e) {
            // Not JSON, or an empty body. Fall through to the generic message.
        }
        return 'The search could not be completed. Please check your filters and try again.';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const secondsAgo = Math.floor((now - date) / 1000);

        const intervals = [
            { label: 'year', seconds: 31536000 },
            { label: 'month', seconds: 2592000 },
            { label: 'day', seconds: 86400 },
            { label: 'hour', seconds: 3600 },
            { label: 'minute', seconds: 60 },
            { label: 'second', seconds: 1 },
        ];

        for (let i = 0; i < intervals.length; i++) {
            const interval = intervals[i];
            const count = Math.floor(secondsAgo / interval.seconds);
            if (count >= 1) {
                return `${count} ${interval.label}${count !== 1 ? 's' : ''} ago`;
            }
        }
        return 'just now';
    };


    const handleSearch = () => {
        // Apply structured filters — clear text search since they are separate modes
        const filtersWithoutText = { ...filters, text: '' };
        setFilters(filtersWithoutText);
        setPage(0);
        updateUrlParams(filtersWithoutText, 0);
        fetchVulnerabilities({ text: '' });
    };

    const handleSearchButton = () => {
        // Text search — clear all structured filters since they are separate modes
        const textOnly = {
            text: filters.text, assigner: '', product: '', vendor: '', advisory: '',
            fromDate: '', toDate: '', fromUpdatedDate: '', toUpdatedDate: '',
            fromKEVDate: '', toKEVDate: '',
            fromScore: 0.0, toScore: 10.0, fromEpss: 0.0, toEpss: 100.0,
            exploited: false
        };
        setFilters(textOnly);
        setPage(0);
        updateUrlParams(textOnly, 0);
        fetchVulnerabilities(textOnly);
    };

    // Update resetFilters to clear filters and trigger search
    const resetFilters = () => {
        setFilters({
            assigner: '',
            product: '',
            vendor: '',
            advisory: '',
            text: '',
            fromDate: '',
            toDate: '',
            fromUpdatedDate: '',
            toUpdatedDate: '',
            fromKEVDate: '',
            toKEVDate: '',
            fromScore: 0.0,
            toScore: 10.0,
            fromEpss: 0.0,
            toEpss: 100.0,
            exploited: false
        });
        setFromScore(0.0);
        setToScore(10.0);
        setFromEpss(0.0);
        setToEpss(100.0);
        setPage(0); // Reset page to 0
        setSize(10); // Reset size to 10
        setIsFilterVisible(false);
        setIsDateFilterVisible(false);
        setIsDateUpdatedFilterVisible(false);
        setIsDateKEVFilterVisible(false);
        setIsScoreFilterVisible(false);
        setIsEpssFilterVisible(false);
        navigate('/search', { replace: true });
        // Launch the search again with empty filters
        fetchVulnerabilities({
            assigner: '',
            product: '',
            vendor: '',
            advisory: '',
            text: '',
            fromDate: '',
            toDate: '',
            fromUpdatedDate: '',
            toUpdatedDate: '',
            fromKEVDate: '',
            toKEVDate: '',
            fromScore: '',
            toScore: '',
            fromEpss: '',
            toEpss: '',
            exploited: false
        });
    };

    const handleClick = (e) => {
        e.preventDefault();
        //toggleFilterVisibility.call(this, '.search-selects, .search-btns');
        console.log('The link was clicked.');
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFilters(prevFilters => ({
            ...prevFilters,
            [name]: value
        }));
    };

    const handleFromDateChange = (date) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            fromDate: date
        }));
    };

    const handleToDateChange = (date) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            toDate: date
        }));
    };

    const handleFromUpdatedDateChange = (date) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            fromUpdatedDate: date
        }));
    };

    const handleToUpdatedDateChange = (date) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            toUpdatedDate: date
        }));
    };

    const handleFromKEVDateChange = (date) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            fromKEVDate: date
        }));
    };

    const handleToKEVDateChange = (date) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            toKEVDate: date
        }));
    };



    const validateDates = () => {
        const { fromDate, toDate } = filters;
        if (fromDate && toDate) {
            const minDate = new Date(fromDate);
            const maxDate = new Date(toDate);

            if (minDate > maxDate) {
                setErrorMessageDateRange('The start date must be before or equal to the end date.');
                setFilters(prevFilters => ({
                    ...prevFilters,
                    fromDate: '',
                    toDate: ''
                }));
            } else {
                setErrorMessageDateRange('');
            }
        }
    };

    useEffect(() => {
        validateDates();
    }, [filters.fromDate, filters.toDate]);

    const validateUpdatedDates = () => {
        const { fromUpdatedDate, toUpdatedDate } = filters;
        if (fromUpdatedDate && toUpdatedDate) {
            const minDate = new Date(fromUpdatedDate);
            const maxDate = new Date(toUpdatedDate);

            if (minDate > maxDate) {
                setErrorMessageUpdatedDateRange('The start date must be before or equal to the end date.');
                setFilters(prevFilters => ({
                    ...prevFilters,
                    fromUpdatedDate: '',
                    toUpdatedDate: ''
                }));
            } else {
                setErrorMessageUpdatedDateRange('');
            }
        }
    };

    useEffect(() => {
        validateUpdatedDates();
    }, [filters.fromUpdatedDate, filters.toUpdatedDate]);

    const validateKEVDates = () => {
        const { fromKEVDate, toKEVDate } = filters;
        if (fromKEVDate && toKEVDate) {
            const minDate = new Date(fromKEVDate);
            const maxDate = new Date(toKEVDate);

            if (minDate > maxDate) {
                setErrorMessageKEVDateRange('The start date must be before or equal to the end date.');
                setFilters(prevFilters => ({
                    ...prevFilters,
                    fromKEVDate: '',
                    toKEVDate: ''
                }));
            } else {
                setErrorMessageKEVDateRange('');
            }
        }
    };

    useEffect(() => {
        validateKEVDates();
    }, [filters.fromKEVDate, filters.toKEVDate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters(prevFilters => ({
            ...prevFilters,
            [name]: value
        }));
    };

    const handleFromScoreChange = (e) => {
        if (e.target.value >=0.0 && e.target.value <= 10.0){
            setFromScore(parseFloat(e.target.value));
            setFilters(prevFilters => ({
                ...prevFilters,
                fromScore: parseFloat(e.target.value)
            }));
        } else {
            setErrorMessageScoreRange('Values must be between 0.0 and 10.0.');
        }
    };

    const handleToScoreChange = (e) => {
        if (e.target.value >= 0.0 && e.target.value <= 10.0){
            setToScore(parseFloat(e.target.value));
            setFilters(prevFilters => ({
                ...prevFilters,
                toScore: parseFloat(e.target.value)
            }));
        } else {
            setErrorMessageScoreRange('Values must be between 0.0 and 10.0.');
        }
    };

    const handleFromEpssChange = (e) => {
        if (e.target.value >= 0.0 && e.target.value <= 100.0){
            setFromEpss(parseFloat(e.target.value));
            setFilters(prevFilters => ({
                ...prevFilters,
                fromEpss: parseFloat(e.target.value)
            }));
        } else {
            setErrorMessageEpssRange('The EPSS scores values should be in the range from 0 to 100 inclusive.');
        }
    };

    const handleToEpssChange = (e) => {
        if (e.target.value >= 0.0 && e.target.value <= 100.0){
            setToEpss(parseFloat(e.target.value));
            setFilters(prevFilters => ({
                ...prevFilters,
                toEpss: parseFloat(e.target.value)
            }));
        } else {
            setErrorMessageEpssRange('The EPSS scores values should be in the range from 0 to 100 inclusive.');
        }
    };

    const validateScores = () => {
        const { fromScore, toScore } = filters;
        if (Number(fromScore) > Number(toScore)) {
            setErrorMessageScoreRange('The minimum score must be less than or equal to the maximum score.');
            setFilters(prevFilters => ({
                ...prevFilters,
                fromScore: 0.0,
                toScore: 10.0
            }));
        } else {
            setErrorMessageScoreRange('');
        }
    };

    const validateEpss = () => {
        const { fromEpss, toEpss } = filters;
        if (Number(fromEpss) > Number(toEpss)) {
            setErrorMessageEpssRange('The minimum score must be less than or equal to the maximum score.');
            setFilters(prevFilters => ({
                ...prevFilters,
                fromEpss: 0.0,
                toEpss: 100.0
            }));
        } else {
            setErrorMessageEpssRange('');
        }
    };

    useEffect(() => {
        validateScores();
    }, [filters.fromScore, filters.toScore]);

    useEffect(() => {
        validateEpss();
    }, [filters.fromEpss, filters.toEpss]);

    const toggleClass = (e) => {
        const currentState = this.state.active;
        this.setState({ active: !currentState });
    }

    const toggleDateFilterVisibility = () => {
        setIsDateFilterVisible(prevState => !prevState);
    };

    const toggleDateUpdatedFilterVisibility = () => {
        setIsDateUpdatedFilterVisible(prevState => !prevState);
    };

    const toggleDateKEVFilterVisibility = () => {
        setIsDateKEVFilterVisible(prevState => !prevState);
    };

    const toggleScoreFilterVisibility = () => {
        setIsScoreFilterVisible(prevState => !prevState);
    };

    const toggleEpssFilterVisibility = () => {
        setIsEpssFilterVisible(prevState => !prevState);
    };

    const handleToggleFilter = () => {
        setIsFilterVisible(!isFilterVisible);
        // Toggle the 'active' class on the filter button
        const filterButton = document.getElementById('filterByBtn');
        filterButton.classList.toggle('active');
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage !== page) {
            console.log(`Changing to page ${newPage}`);
            setPage(newPage);
            updateUrlParams(filters, newPage);
        }
    };

    useEffect(() => {
        console.log(`Fetching data for page ${page}`);
        fetchVulnerabilities(); // Fetch data when page changes
    }, [page]); // Add page as a dependency

    useEffect(() => {
        fetchVulnerabilities({ text: initialText }); // Fetch data when page changes
    }, [initialText]); // Add page as a dependency


    const renderPaginationButtons = () => {
        const buttons = [];

        // Previous button
        buttons.push(
            <button key="previous" disabled={page <= 0} onClick={() => handlePageChange(page - 1)}>
                <span className="sr-only">Previous</span>
            </button>
        );

        // First page button
        buttons.push(
            <button key="first" disabled={page === 0} onClick={() => handlePageChange(0)}>1</button>
        );

        // Ellipsis before current page if necessary
        if (page > 2) {
            buttons.push(<span key="ellipsis-prev">...</span>);
        }

        // Previous page button if available
        if (page > 1) {
            buttons.push(
                <button key={page - 1} onClick={() => handlePageChange(page - 1)}>{page}</button>
            );
        }

        // Current page (not a button)
        buttons.push(
            <span key="current" className="current-page">{page + 1}</span>
        );

        // Next page button if available
        if (page < totalPages - 1) {
            buttons.push(
                <button key={page + 1} onClick={() => handlePageChange(page + 1)}>{page + 2}</button>
            );
        }

        // Ellipsis after current page if necessary
        if (page < totalPages - 3) {
            buttons.push(<span key="ellipsis-next">...</span>);
        }

        // Last page button
        buttons.push(
            <button key="last" disabled={page === totalPages - 1} onClick={() => handlePageChange(totalPages - 1)}>Last page</button>
        );

        // Next button
        buttons.push(
            <button key="next" disabled={page >= totalPages - 1} onClick={() => handlePageChange(page + 1)}>
                <span className="sr-only">Next</span>
            </button>
        );

        return buttons;
    };


    const getVendorInfo = (result) => {
        if (!result.enisaIdVendor || result.enisaIdVendor.length === 0) {
           // return 'No vendors'; // Return a default message or handle this case as required
           return '-';
        }

        if (result.enisaIdVendor.length === 1) {
          return result.enisaIdVendor[0].vendor.name; // If there's only one vendor, return its name
        } else {
          return `${result.enisaIdVendor.length} vendors`; // Otherwise, return the number of vendors
        }
    };
    const renderAlternativeIDs = (result) => {
        let altIdList= result.aliases.split('\n');
        let arrLength= altIdList.length - 1;
        if(altIdList[arrLength] == ''){
          altIdList.pop();
        }
        return altIdList.map((alternative) => (
          <p className='alternative-id-li'>{alternative}</p>
        ));
    };

    // Tooltip text for the grey honeypot icon in the Exploitation column.
    // Kept minimal — full breakdown lives in the detail-page table.
    const buildHoneypotListingTooltip = () => 'Seen in Honeypot';

    // Fetch KEV entries for exploited results
    useEffect(() => {
        const exploitedResults = results.filter(r => r.exploitedSince);
        if (exploitedResults.length === 0) return;

        const ids = [...new Set(exploitedResults.map(v => v.id))];
        const fetchKevBatch = async () => {
            try {
                const response = await fetch(`${appUrl}/kevEntries/batch?ids=${ids.join(',')}`);
                if (response.ok) {
                    const data = await response.json();
                    setKevMap(data);
                }
            } catch (error) {
                console.error('Error fetching KEV batch:', error);
            }
        };
        fetchKevBatch();
    }, [results]);

    // Fetch Shadowserver honeypot observations for every result on the
    // page. Unlike KEV, honeypot activity can be present without exploitedSince being set,
    // so we batch on the full id set rather than the exploited subset.
    useEffect(() => {
        if (!results || results.length === 0) return;
        const ids = [...new Set(results.map(v => v.id))];
        const fetchHoneypotBatch = async () => {
            try {
                const response = await fetch(`${appUrl}/honeypotObservations/batch?ids=${ids.join(',')}`);
                if (response.ok) {
                    const data = await response.json();
                    setHoneypotMap(data || {});
                }
            } catch (error) {
                console.error('Error fetching honeypot batch:', error);
            }
        };
        fetchHoneypotBatch();
    }, [results]);

    useEffect(() => {
          document.title = "Search | EUVD";
        }, []);

    return (
        <div className="vulnerabilities-list-page">
            <Header />
            <InfoBanner />
            <main>

                <section className="banner">
                    <nav className="breadcrumbs container-1200">
                        <ul>
                            <li><a href="/homepage">Home</a></li>
                            <li><a href="">Vulnerability List</a></li>
                        </ul>
                    </nav>
                </section>

                <section className="container-1200 container-up ">
                    <div>
                        <h1 className="hidden">ENISA Vulnerability Database</h1>
                        <h2>Vulnerability List</h2>
                    </div>
                    <div className="filter-and-list-wrapper">
                        <div className="filters">
                            <div className="button-wrapper">
                                <button onClick={() => {
                                    const vulnerabilityFilter = { fromScore: 9.0, toScore: 10.0 };
                                    const mergedFilters = { ...filters, ...vulnerabilityFilter };
                                    setFilters(a => ({ ...a, ...vulnerabilityFilter }));
                                    setFromScore(9.0);
                                    setToScore(10.0);
                                    setPage(0);
                                    updateUrlParams(mergedFilters, 0);
                                    fetchVulnerabilities(vulnerabilityFilter);
                                }} className='btn-secondary filter-button filter-button-first'>Rated critical</button>
                                <button onClick={() => {
                                    const vulnerabilityFilter = { exploited: true };
                                    const mergedFilters = { ...filters, ...vulnerabilityFilter };
                                    setFilters(a => ({ ...a, ...vulnerabilityFilter }));
                                    setPage(0);
                                    updateUrlParams(mergedFilters, 0);
                                    fetchVulnerabilities(vulnerabilityFilter);
                                }}
                                    className='btn-secondary filter-button filter-button-second'>Known exploited</button>
                            </div>

                            <form action="#">
                                <fieldset className="search-selects" > 
                                    <div className="selects-container">
                                        <div className="ranges-wrapper">
                                            <div> 
                                                    <label className='filter-label has-tooltip relative' for="toggleFilterByRange" id='cvssFilterLabel'>Filter by CVSS score<Tooltip text="The Common Vulnerability Scoring System (CVSS) provides a way to capture the principal characteristics of a vulnerability and produce a numerical score reflecting its severity."><span className='info'></span> </Tooltip></label>                                             
                                                    <fieldset className="search-ranges" aria-labelledby='cvssFilterLabel'>
                                                        <legend>Filter by CVSS score</legend>
                                                        {/*<p className="legend" id="toggleFilterByRange" onClick={toggleScoreFilterVisibility}>Filter by CVSS score</p>
                                                        {isScoreFilterVisible && () if }*/}
                                                            <div className="ranges-toggle">
                                                                <div className="ranges-container">
                                                                    <div className="range-container">
                                                                        <label id='cvssMinimunLabel' for="fromScore" htmlFor="scoreMin">Minimum score <span id="scoreMinValue">{filters.fromScore}</span></label>
                                                                        <input aria-labelledby='cvssMinimunLabel' type="number" inputMode='decimal' step={0.1} id="fromScore" name="fromScore" min="0.0" max="10.0" value={filters.fromScore} onChange={handleFromScoreChange} />
                                                                    </div>
                                                                    <div className="range-container">
                                                                        <label id='cvssMaxLabel' for="toScore" htmlFor="scoreMax">Maximum score <span id="scoreMaxValue">{filters.toScore}</span></label>
                                                                        <input aria-labelledby='cvssMaxLabel' type="number" step={0.1} id="toScore" name="toScore" min={0.0} max={10.0} value={filters.toScore} onChange={handleToScoreChange} />
                                                                    </div>
                                                                    {errorMessageScoreRange && <p className="error-message-date">{errorMessageScoreRange}</p>}
                                                                </div>
                                                            </div>
                                                        
                                                        {/*<datalist id="scoreMarkers">
                                                            <option value="1" label="1"></option>
                                                            <option value="2" label="2"></option>
                                                            <option value="3" label="3"></option>
                                                            <option value="4" label="4"></option>
                                                            <option value="5" label="5"></option>
                                                            <option value="6" label="6"></option>
                                                            <option value="7" label="7"></option>
                                                            <option value="8" label="8"></option>
                                                            <option value="9" label="9"></option>
                                                            <option value="10" label="10"></option>
                                                        </datalist>*/}
                                                    </fieldset>
                                                
                                            </div>
                                            <div >
                                                <label className='filter-label has-tooltip relative' for="toggleFilterByEpssRange" id='epssFilterLabel'>Filter by EPSS score <Tooltip text="Exploit Prediction Scoring System (EPSS) from FIRST. The EPSS score is representing the probability of exploitation in the wild in the next 30 days."><span className='info'></span> </Tooltip></label>
                                                <fieldset className="search-ranges" aria-labelledby='epssFilterLabel'>
                                                        <legend>Filter by EPSS score</legend>
                                                        {/*<p className="legend" id="toggleFilterByEpssRange" onClick={toggleEpssFilterVisibility}>Filter by EPSS score</p>
                                                        {isEpssFilterVisible && ()} */}
                                                            <div className="ranges-toggle">
                                                                <div className="ranges-container">
                                                                    <div className="range-container">
                                                                        <label id='epssMinLabel' for="fromEpss" htmlFor="scoreMin">Minimum score <span id="scoreMinValue">{filters.fromEpss}</span></label>
                                                                        <input aria-labelledby='epssMinLabel' type="number" id="fromEpss" name="fromEpss" step={0.1} value={filters.fromEpss} onChange={handleFromEpssChange} list="epssScoreMarkers" min={0.0} max={100.0}/>
                                                                    </div>
                                                                    <div className="range-container">
                                                                        <label id='epssMaxLabel' for="toEpss" htmlFor="scoreMax">Maximum score <span id="scoreMaxValue">{filters.toEpss}</span></label>
                                                                        <input aria-labelledby='epssMaxLabel' type="number" id="toEpss" name="toEpss" step={0.1} value={filters.toEpss} onChange={handleToEpssChange} list="epssScoreMarkers" min={0.0} max={100.0}/>
                                                                    </div>
                                                                    {errorMessageEpssRange && <p className="error-message-date">{errorMessageEpssRange}</p>}
                                                                </div>                                                              
                                                            </div>
                                                        
                                                    </fieldset>
                                                
                                            </div>
                                        </div>
                                        <div></div>
                                        <label className='filter-label' for="vendor" id='filterByVendorLabel'>Filter by vendor</label>
                                        <input className='w-padding' type="text" name="vendor" id="vendor" placeholder="Search by vendor"
                                            aria-labelledby="filterByVendorLabel" onChange={handleChange} value={filters.vendor} />
                                        <label className='filter-label' for="product" id='filterByProductLabel'>Filter by product</label>
                                        <input className='w-padding' type="text" name="product" id="product"
                                            placeholder="Search by product" aria-labelledby="filterByProductLabel" onChange={handleChange} value={filters.product} />
        
                                        <label className='filter-label has-tooltip relative' for="assigner" id='filterByAssignerLabel'>Filter by assigner <Tooltip text="The identifier assigning CVE Numbering Authority (CNA)."><span className='info'></span> </Tooltip></label>
                                        <input className='w-padding' type="text" name="assigner" id="assigner" placeholder="Search by assigner" aria-labelledby="filterByAssignerLabel" onChange={handleChange} value={filters.assigner} />

                                        {/*<label className='filter-label' for="advisory" id='filterByAdvisoryLabel'>Filter by advisory</label>
                                        <input className='w-padding' type="text" name="advisory" id="advisory" placeholder="Search by advisory"
                                            aria-labelledby="filterByAdvisoryLabel" onChange={handleChange} value={filters.advisory} />*/}

                                        <label className='filter-label' for="toggleFilterByDate" id='filterPublicationDateLabel'>Filter by publication date</label>
                                        <fieldset className="search-dates" aria-labelledby='filterPublicationDateLabel'>
                                            <legend>Filter by publication date <span className=''></span></legend>
                                            <p className="legend" id="toggleFilterByDate" onClick={toggleDateFilterVisibility}>
                                            Filter by publication date
                                            </p>
                                            {isDateFilterVisible && (
                                                <div className="dates-toggle">
                                                    <div className="dates-container">
                                                        <div className="date-container">
                                                            <label htmlFor="startDate">Start date</label>
                                                            <input aria-label="Date" type="date" id="fromDate" onChange={(e) => handleFromDateChange(e.target.value)} value={filters.fromDate} />
                                                        </div>
                                                        <div className="date-container">
                                                            <label htmlFor="endDate">End date</label>
                                                            <input aria-label="Date" type="date" id="toDate" onChange={(e) => handleToDateChange(e.target.value)} value={filters.toDate} />
                                                        </div>
                                                        {errorMessageDateRange && <p className="error-message-date">{errorMessageDateRange}</p>}
                                                    </div>
                                                    
                                                </div>
                                            )}
                                        </fieldset>
                                        <label className='filter-label' for="toggleFilterByUpdatedDate" id='filterUpdatedDateLabel'>Filter by updated date</label>
                                        <fieldset className="search-dates" aria-labelledby='filterUpdatedDateLabel'>
                                            <legend>Filter by updated date <span className=''></span></legend>
                                            <p className="legend" id="toggleFilterByUpdatedDate" onClick={toggleDateUpdatedFilterVisibility}>
                                            Filter by updated date
                                            </p>
                                            {isDateUpdatedFilterVisible && (
                                                <div className="dates-toggle">
                                                    <div className="dates-container">
                                                        <div className="date-container">
                                                            <label htmlFor="startUpdatedDate">Start date</label>
                                                            <input aria-label="Date" type="date" id="fromUpdatedDate" onChange={(e) => handleFromUpdatedDateChange(e.target.value)} value={filters.fromUpdatedDate} />
                                                        </div>
                                                        <div className="date-container">
                                                            <label htmlFor="endUpdatedDate">End date</label>
                                                            <input aria-label="Date" type="date" id="toUpdatedDate" onChange={(e) => handleToUpdatedDateChange(e.target.value)} value={filters.toUpdatedDate} />
                                                        </div>
                                                        {errorMessageUpdatedDateRange && <p className="error-message-date">{errorMessageUpdatedDateRange}</p>}
                                                    </div>

                                                </div>
                                            )}
                                        </fieldset>

                                        {/*<label className='filter-label' for="toggleFilterByKEVDate" id='filterKEVDateLabel'>Filter by KEV date</label>
                                        <fieldset className="search-dates" aria-labelledby='filterKEVDateLabel'>
                                            <legend>Filter by KEV date <span className=''></span></legend>
                                            <p className="legend" id="toggleFilterByKEVDate" onClick={toggleDateKEVFilterVisibility}>
                                            Filter by KEV date
                                            </p>
                                            {isDateKEVFilterVisible && (
                                                <div className="dates-toggle">
                                                    <div className="dates-container">
                                                        <div className="date-container">
                                                            <label htmlFor="startKEVDate">Start date</label>
                                                            <input aria-label="Date" type="date" id="fromKEVDate" onChange={(e) => handleFromKEVDateChange(e.target.value)} value={filters.fromKEVDate} />
                                                        </div>
                                                        <div className="date-container">
                                                            <label htmlFor="endKEVDate">End date</label>
                                                            <input aria-label="Date" type="date" id="toKEVDate" onChange={(e) => handleToKEVDateChange(e.target.value)} value={filters.toKEVDate} />
                                                        </div>
                                                        {errorMessageKEVDateRange && <p className="error-message-date">{errorMessageKEVDateRange}</p>}
                                                    </div>

                                                </div>
                                            )}
                                        </fieldset>*/}

                                        <div className='exploited-checkbox-wrapper-div'>
                                                <label className='filter-label has-tooltip relative' for="exploitedCheckbox" id='exploitedFilterLabel'>Show only exploited vulnerabilities <Tooltip text="As indicated by an authoritative source, known exploitation indicates a vulnerability either has been exploited or is currently under active exploitation."><span className='info'></span> </Tooltip></label>
                                                <div className='exploited-checkbox-wrapper' aria-labelledby='exploitedFilterLabel'>
                                                    <label>
                                                        <input
                                                            id='exploitedCheckbox'
                                                            type="checkbox"
                                                            checked={filters.exploited}
                                                            onChange={(e) => setFilters({ ...filters, exploited: e.target.checked })}
                                                        />
                                                        Exploited
                                                    </label>
                                                </div>                                           
                                        </div>
                                    </div>
                                </fieldset>
                               
                                <fieldset className="search-btns" >
                                    <div className="button-container">
                                        <legend>Apply or reset</legend>
                                        <button type="button" id="applyButton" onClick={(e) => handleSearch(e)}>Apply</button>
                                        <button type="button" id="resetButton" onClick={resetFilters} aria-label="Reset filters button">Reset filters</button>
                                    </div>
                                </fieldset>
                            </form>
                        </div>
                        <table className="list-of-vulnerabilities home search">
                            <thead>
                                <tr className="table-header">
                                    <th>ID</th>
                                    <th>Alternative ID</th>
                                    <th>Exploitation</th>
                                    <th>CVSS</th>
                                    <th>Product</th>
                                    <th>Vendor</th>
                                    <th>Changed</th>
                                    {/*<th>Assigner</th>*/}
                                </tr>
                            </thead>
                            <tbody>
                                {/* Conditionally render loading animation */}
                                {isLoading ? (
                                    <div className="spinner-container"><div className="loading-spinner"></div></div>
                                ) : searchError ? ( // A rejected or failed search, not an empty one
                                    <div className="no-results-message" role="alert">{searchError}</div>
                                ) : results.length === 0 ? ( // Check if results are empty
                                    <div className="no-results-message">No results found.</div>
                                ) : (
                                    results.map((result, index) => (
                                        <><tr className={`vulnerability-item ${index % 2 ? 'blue-ten' : 'clear'}`} key={result.id}>
                                            <td data-header="ID" scope='name' className="top-item-data cve-wrapper">
                                                <Link activeClassName="cve" to={`/vulnerability/${result.id}`}>
                                                    <p><strong>{result.id}</strong></p>
                                                </Link>
                                            </td>
                                            <td data-header="alternative ID" className="alt-id-column">
                                                {result.aliases && (renderAlternativeIDs(result))}
                                            </td>
                                            {result.exploitedSince ? (
                                                <td data-header="Exploitation" className="exploited">
                                                    <div className="kev-row">
                                                        {(kevMap[result.id] && kevMap[result.id].length > 0) ? (
                                                            <div className="cisa-kev">
                                                                <p className='exploited-icon' title={kevMap[result.id].map(e => e.kevSource ? e.kevSource.displayName : 'KEV').join('\n')}></p>
                                                            </div>
                                                        ) : (
                                                            <div className="cisa-kev">
                                                                <p className='exploited-icon' title="CISA Known Exploited Vulnerabilities Catalog"></p>
                                                            </div>
                                                        )}
                                                        {(honeypotMap[result.id] && honeypotMap[result.id].length > 0) && (
                                                            <div className="cisa-kev">
                                                                <p className='exploited-icon-honeypot' title={buildHoneypotListingTooltip(honeypotMap[result.id][0])}></p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            ) : (honeypotMap[result.id] && honeypotMap[result.id].length > 0) ? (
                                                <td data-header="Exploitation" className="exploited">
                                                    <div className="kev-row">
                                                        <div className="cisa-kev">
                                                            <p className='exploited-icon-honeypot' title={buildHoneypotListingTooltip(honeypotMap[result.id][0])}></p>
                                                        </div>
                                                    </div>
                                                </td>
                                            ) : (
                                                <td data-header="Exploitation" className={`epss score ${result.epss ? 'score-' + Math.floor(result.epss / 10) : 'no-score'}`}>
                                                   {result.epss ? (
                                                    <p><span><strong><span>{(result.epss).toFixed(2)}</span></strong>%</span></p>
                                                   ):(
                                                    <p>Not available</p>
                                                   )}

                                                </td>
                                            )}
                                            <td data-header="CVSS" className={`score ${result.baseScore ? 'score-' + Math.round(result.baseScore) : 'no-score'}`}>
                                                {result.baseScoreVersion ?? '' !== '' ? (
                                                    <p><span>v{result.baseScoreVersion}: <strong>{result.baseScore}</strong></span></p>
                                                ) : (
                                                    <p>Not available</p>
                                                )}
                                            </td>
                                            <td data-header="Product" className="product">
                                                {result.enisaIdProduct.length > 0 ? (
                                                    <p>{result.enisaIdProduct[0].product.name}</p>
                                                ) : (
                                                    <p>-</p>
                                                )}
                                            </td>

                                            <td data-header="Vendor" className="vendor-info">
                                                <p>{getVendorInfo(result)}</p>
                                            </ td>

                                            <td data-header="Changed" className="date">
                                                <p><span>{formatDate(result.dateUpdated)}</span></p>
                                            </ td>
                                            {/*<td data-header="Assigner" className="csirt">
                                                {result.assigner && (
                                                    <p><span>{result.assigner}</span></p>
                                                )}
                                            </td>*/}
                                        </tr>
                                            <tr className={`${index % 2 ? 'blue-ten' : 'clear'}`}>
                                                <td data-header="Description" className="description" colspan="8">
                                                    <div>
                                                        <p>{result.description}</p>
                                                    </div>
                                                </td>
                                            </tr></>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {results.length > 0 && (
                        <div className="pagination-container">
                            <div className="pagination">
                                {renderPaginationButtons()}
                            </div>
                        </div>
                    )}
                </section>
                <Banner />
            </main>
            <Footer />
        </div>
    );
};

export default SearchPage;
